import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { authRedirectError, randomRoomCode, randomSeed } from './codes';
import { isSupabaseConfigured, type SupabaseConfig } from './config';
import type { LobbyApi } from './lobbyApi';
import {
  parseLadder,
  parseMyMatches,
  parseProfile,
  parseRoom,
  parseRoomList,
  type Account,
  type LadderEntry,
  type MyMatch,
  type OnlineRoom,
} from './parse';

const ROOM_COLUMNS =
  'id, code, host_id, guest_id, host_name, guest_name, host_visitor, guest_visitor, status, seed, map_id';

export function extractErrorMessage(error: unknown, depth = 0): string {
  if (typeof error === 'string' && error.trim()) return error.trim();
  if (error && typeof error === 'object') {
    const raw = error as {
      message?: unknown;
      details?: unknown;
      hint?: unknown;
      error?: unknown;
      code?: unknown;
    };
    if (depth < 2 && raw.error && typeof raw.error === 'object' && raw.error !== error) {
      const nested = extractErrorMessage(raw.error, depth + 1);
      if (nested !== 'Something went wrong.') return nested;
    }
    const parts = [raw.message, raw.details, raw.hint].filter(
      (part): part is string => typeof part === 'string' && part.trim().length > 0,
    );
    if (parts.length > 0) return parts.join(' ');
    if (typeof raw.error === 'string' && raw.error.trim()) return raw.error.trim();
    if (typeof raw.code === 'string' && raw.code.trim()) return raw.code.trim();
  }
  return 'Something went wrong.';
}

export function onlineError(error: unknown): string {
  const message = extractErrorMessage(error);
  if (/already registered|already been registered|user already exists/i.test(message)) {
    return 'That email is already registered. Log in.';
  }
  if (/invalid login|invalid credentials/i.test(message)) {
    return 'Email or password is wrong.';
  }
  if (/email not confirmed|email_not_confirmed/i.test(message)) {
    return 'Email should be confirmed.';
  }
  if (/already in a match/i.test(message)) {
    return 'You already have a match in progress. Resume it within 10 seconds if you disconnected.';
  }
  if (
    /push_match_inputs|pull_match_inputs|push_match_signal|pull_match_signals|record_match|points_for|points_against|schema cache|column .* does not exist/i.test(
      message,
    )
  ) {
    return 'Online play needs a database update. Run pnpm migrate.';
  }
  if (
    /already taken/i.test(message) ||
    (/display_name/i.test(message) && /duplicate|unique/i.test(message))
  ) {
    return 'That name is already taken.';
  }
  if (/failed to fetch|load failed|network/i.test(message)) {
    return 'Could not reach Supabase. Check the project URL.';
  }
  if (/jwt|not authenticated|sign in first/i.test(message)) return 'Sign in again.';
  if (/no longer open/i.test(message)) return 'That room was just taken or closed.';
  if (/otp_expired/i.test(message)) return 'That email link expired. Sign in with your password.';
  if (/not ready/i.test(message)) return 'The room is not ready to start.';
  if (/reading ['"]ready['"]/i.test(message)) {
    return 'The match could not start. Leave and open a new room.';
  }
  if (/callbacks for realtime:.*after ['"]subscribe\(\)['"]/i.test(message)) {
    return 'Could not watch the room. Leave and join again.';
  }
  if (
    /match is not running|room missing|not your match|bad result|bad winner|bad score/i.test(
      message,
    )
  ) {
    return 'Could not save the score.';
  }
  return message;
}

/** Match-over line for an online room. Never paint a generic crash string. */
export function scoreSaveCopy(note: string): string {
  const trimmed = note.trim();
  if (!trimmed || /^saving/i.test(trimmed)) return 'Saving the score…';
  if (/^something went wrong\.?$/i.test(trimmed)) return 'Could not save the score.';
  return trimmed;
}

/**
 * Supabase reuses a Realtime channel when the topic matches. A second
 * `.on('postgres_changes')` after `subscribe()` throws. Lobby and the live
 * match both watch the same room, so every watch needs its own topic.
 */
export function nextRealtimeTopic(kind: string, key: string): string {
  return `${kind}:${key}:${crypto.randomUUID()}`;
}

export class OnlineService implements LobbyApi {
  private constructor(private readonly supabase: SupabaseClient) {}

  static connect(config: SupabaseConfig): OnlineService | null {
    if (!isSupabaseConfigured(config)) return null;
    if (typeof window !== 'undefined' && authRedirectError(window.location.hash)) {
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState(null, '', url);
    }
    return new OnlineService(
      createClient(config.url, config.publishableKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      }),
    );
  }

  onAuth(listener: (account: Account | null) => void): () => void {
    const { data } = this.supabase.auth.onAuthStateChange((_event, session) => {
      setTimeout(() => {
        if (!session?.user) {
          listener(null);
          return;
        }
        void this.accountFromUser(session.user.id, session.user.email ?? '')
          .then(listener)
          .catch(() => listener(null));
      }, 0);
    });
    return () => data.subscription.unsubscribe();
  }

  async signUp(
    email: string,
    password: string,
    displayName: string,
  ): Promise<{ account: Account | null; pendingEmail: boolean }> {
    const { data, error } = await this.supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { data: { display_name: displayName } },
    });
    if (error) throw new Error(onlineError(error));
    if (!data.session || !data.user) return { account: null, pendingEmail: true };
    return {
      account: await this.accountFromUser(data.user.id, data.user.email ?? email),
      pendingEmail: false,
    };
  }

  async signIn(email: string, password: string): Promise<Account> {
    const { data, error } = await this.supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) throw new Error(onlineError(error));
    if (!data.user) throw new Error('Sign in failed.');
    return this.accountFromUser(data.user.id, data.user.email ?? email);
  }

  async accessToken(): Promise<string | null> {
    const { data, error } = await this.supabase.auth.getSession();
    if (error || !data.session?.access_token) return null;
    return data.session.access_token;
  }

  async reloadAccount(): Promise<Account | null> {
    const { data, error } = await this.supabase.auth.getUser();
    if (error || !data.user) return null;
    return this.accountFromUser(data.user.id, data.user.email ?? '');
  }

  async signOut(): Promise<void> {
    const { error } = await this.supabase.auth.signOut();
    if (error) throw new Error(onlineError(error));
  }

  async listOpenRooms(): Promise<OnlineRoom[]> {
    const { data, error } = await this.supabase.rpc('list_open_rooms');
    if (error) throw new Error(onlineError(error));
    return parseRoomList(data);
  }

  async leaderboard(): Promise<LadderEntry[]> {
    const { data, error } = await this.supabase.rpc('leaderboard');
    if (error) throw new Error(onlineError(error));
    return parseLadder(data);
  }

  /** Finished person-vs-person matches for this account, newest first. */
  async myMatches(userId: string): Promise<MyMatch[]> {
    const { data, error } = await this.supabase
      .from('match_results')
      .select(
        'room_id, host_id, guest_id, winner_id, orange_score, turquoise_score, rooms(host_name, guest_name)',
      )
      .or(`host_id.eq.${userId},guest_id.eq.${userId}`)
      .order('created_at', { ascending: false })
      .limit(40);
    if (error) throw new Error(onlineError(error));
    return parseMyMatches(userId, data);
  }

  async createRoom(): Promise<OnlineRoom> {
    let last = 'Could not create a room.';
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const { data, error } = await this.supabase.rpc('create_room', {
        p_code: randomRoomCode(),
        p_seed: randomSeed(),
      });
      if (!error) {
        const room = parseRoom(data);
        if (!room) throw new Error('Room could not be created.');
        return room;
      }
      last = onlineError(error);
      if (!/duplicate|unique|code/i.test(error.message)) break;
    }
    throw new Error(last);
  }

  async joinRoom(roomId: string): Promise<OnlineRoom> {
    const { data, error } = await this.supabase.rpc('join_room', { p_room_id: roomId });
    if (error) throw new Error(onlineError(error));
    const room = parseRoom(data);
    if (!room) throw new Error('Room could not be joined.');
    return room;
  }

  async startRoom(roomId: string): Promise<OnlineRoom> {
    const { data, error } = await this.supabase.rpc('start_room', { p_room_id: roomId });
    if (error) throw new Error(onlineError(error));
    const room = parseRoom(data);
    if (!room) throw new Error('Room could not start.');
    return room;
  }

  async leaveRoom(roomId: string): Promise<void> {
    const { error } = await this.supabase.rpc('leave_room', { p_room_id: roomId });
    if (error) throw new Error(onlineError(error));
  }

  async roomByCode(code: string): Promise<OnlineRoom | null> {
    const { data, error } = await this.supabase
      .from('rooms')
      .select(ROOM_COLUMNS)
      .eq('code', code)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(onlineError(error));
    return data ? parseRoom(data) : null;
  }

  async roomById(roomId: string): Promise<OnlineRoom | null> {
    const { data, error } = await this.supabase
      .from('rooms')
      .select(ROOM_COLUMNS)
      .eq('id', roomId)
      .maybeSingle();
    if (error) throw new Error(onlineError(error));
    return data ? parseRoom(data) : null;
  }

  /** Lobby row still `playing` for this account (blocks Find / Create). */
  async activePlayingRoom(userId: string): Promise<OnlineRoom | null> {
    const { data, error } = await this.supabase
      .from('rooms')
      .select(ROOM_COLUMNS)
      .eq('status', 'playing')
      .or(`host_id.eq.${userId},guest_id.eq.${userId}`)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(onlineError(error));
    return data ? parseRoom(data) : null;
  }

  watchRooms(onChange: () => void): () => void {
    const channel = this.supabase
      .channel(nextRealtimeTopic('rooms-lobby', 'all'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rooms' }, () => onChange())
      .subscribe();
    return () => {
      void this.supabase.removeChannel(channel);
    };
  }

  watchRoom(roomId: string, onRoom: (room: OnlineRoom) => void): () => void {
    try {
      const channel = this.supabase
        .channel(nextRealtimeTopic('room', roomId))
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'rooms', filter: `id=eq.${roomId}` },
          (payload) => {
            const room = parseRoom(payload.new);
            if (room) onRoom(room);
          },
        )
        .subscribe();
      return () => {
        void this.supabase.removeChannel(channel);
      };
    } catch {
      // Lobby polling still sees joins. Never throw into the match-start path.
      return () => {};
    }
  }

  dispose(): void {
    void this.supabase.removeAllChannels();
  }

  private async accountFromUser(id: string, email: string): Promise<Account> {
    const { data, error } = await this.supabase
      .from('profiles')
      .select('display_name, wins, losses')
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(onlineError(error));
    return parseProfile(id, email, data);
  }
}
